import { type QueryFunctionContext, useQuery } from "@tanstack/react-query";
import type { FC } from "react";
import { getSettlementCaseById, getCaseCorrespondenceWorkFlagById } from "src/pages/UserDashboard/Cases/api/casesApi";
import { CaseSettlementDetails } from "src/pages/UserDashboard/Cases/CaseDetail/CaseSettlementDetail";
import { CaseEscalationDetails } from "src/pages/UserDashboard/Cases/CaseDetail/CaseEscalationDetail";
import { SettlementCase } from "src/types/cases";
import { CaseType, isEscalationCaseType } from "../../case-types";
import { PulseLoader } from "react-spinners";
import { Box } from "@mui/material";
import { CaseItemsTable } from "./CaseItemsGrid";
import { CorrespondenceWorkFlag } from "../../correspondence-work-flags";
import { fetchEscalationCaseById } from "src/pages/UserDashboard/Cases/queries";

export interface CaseDetailsProps {
  caseId: string;
  caseType: CaseType;
}

const fetchSettlementCaseById = async ({
  queryKey: [, caseType, caseId],
}: QueryFunctionContext<["cases", CaseType, string, "details"]>): Promise<SettlementCase> =>
  getSettlementCaseById(caseId, caseType);

const fetchCaseCorrespondenceById = async ({
  queryKey: [, caseId],
}: QueryFunctionContext<["cases", string, "correspondenceWorkFlags"]>): Promise<CorrespondenceWorkFlag[]> =>
  getCaseCorrespondenceWorkFlagById(caseId);

const SettlementCaseDetails: FC<{ caseId: string; caseType: CaseType }> = ({ caseId, caseType }) => {
  const caseQuery = useQuery({
    queryFn: fetchSettlementCaseById,
    queryKey: ["cases", caseType, caseId, "details"],
  });

  const workFlagQuery = useQuery({
    queryFn: fetchCaseCorrespondenceById,
    queryKey: ["cases", caseId, "correspondenceWorkFlags"],
  });

  if (caseQuery.isPending)
    return (
      <Box padding={3}>
        <PulseLoader size="8px" color="#EF1442" />
      </Box>
    );
  if (caseQuery.isError) return <span>Error</span>;
  return (
    <CaseSettlementDetails
      caseItem={caseQuery.data}
      correspondenceWorkFlags={workFlagQuery.data ?? []}
      caseType={caseType}
      isRefreshing={caseQuery.isRefetching}
    />
  );
};

const EscalationCaseDetails: FC<{ caseId: string; caseType: CaseType }> = ({ caseId, caseType }) => {
  const caseQuery = useQuery({
    queryFn: fetchEscalationCaseById,
    queryKey: ["cases", caseType, caseId, "details"],
  });

  const workFlagQuery = useQuery({
    queryFn: fetchCaseCorrespondenceById,
    queryKey: ["cases", caseId, "correspondenceWorkFlags"],
  });

  if (caseQuery.isPending)
    return (
      <Box padding={3}>
        <PulseLoader size="8px" color="#EF1442" />
      </Box>
    );
  if (caseQuery.isError) return <span>Error</span>;
  return (
    <CaseEscalationDetails
      caseItem={caseQuery.data}
      correspondenceWorkFlags={workFlagQuery.data ?? []}
      caseType={caseType}
      isRefreshing={caseQuery.isRefetching}
    />
  );
};

export const CaseDetails: FC<CaseDetailsProps> = ({ caseId, caseType }) => {
  return (
    <Box>
      {isEscalationCaseType(caseType) ? (
        <EscalationCaseDetails caseId={caseId} caseType={caseType} />
      ) : (
        <SettlementCaseDetails caseId={caseId} caseType={caseType} />
      )}
      <Box gridRow={"2"} padding={2}>
        <CaseItemsTable caseId={caseId} caseType={caseType} />
      </Box>
    </Box>
  );
};
